// API Constants
export const API_ENDPOINTS = {
  NODE_API_URI: '/api/portal',
  NODE_API_URL: 'https://localhost:3000',
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  USER: {
    PROFILE: '/user/profile',
    CHANGE_PASSWORD: '/user/change-password',
  }
}

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} 

// Error Codes
export const ERROR_CODES = {
  // Authentication errors
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_REQUIRED: 'TOKEN_REQUIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  
  // Authorization errors
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  ACCESS_DENIED: 'ACCESS_DENIED',
  
  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  REQUIRED_FIELD_MISSING: 'REQUIRED_FIELD_MISSING',
  INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT',
  PASSWORD_TOO_WEAK: 'PASSWORD_TOO_WEAK',
  
  // Resource errors
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS: 'RESOURCE_ALREADY_EXISTS',
  
  // External service errors
  YODLEE_API_ERROR: 'YODLEE_API_ERROR',
  FASTLINK_ERROR: 'FASTLINK_ERROR',
  EXTERNAL_SERVICE_UNAVAILABLE: 'EXTERNAL_SERVICE_UNAVAILABLE',
  
  // General errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
} 

// Account Categories
export const ACCOUNT_CATEGORIES = {
  CASH: {
    CHECKING: 'Checking',
    SAVINGS: 'Savings',
    MONEY_MARKET: 'Money Market',
    CD: 'Certificate of Deposit',
  },
  CREDIT: {
    CREDIT_CARD: 'Credit Card',
    LINE_OF_CREDIT: 'Line of Credit',
  },
  INVESTMENT: {
    BROKERAGE: 'Brokerage',
    RETIREMENT_401K: '401(k)',
    RETIREMENT_IRA: 'IRA',
    RETIREMENT_ROTH: 'Roth IRA',
    PENSION: 'Pension',
  },
  LOAN: {
    MORTGAGE: 'Mortgage',
    HOME_EQUITY: 'Home Equity',
    AUTO_LOAN: 'Auto Loan',
    PERSONAL_LOAN: 'Personal Loan',
    STUDENT_LOAN: 'Student Loan',
  },
  INSURANCE: {
    LIFE: 'Life Insurance',
    HEALTH: 'Health Insurance',
    AUTO: 'Auto Insurance',
    HOME: 'Home Insurance',
  },
}

// Transaction Categories
export const TRANSACTION_CATEGORIES = {
  INCOME: {
    SALARY: 'Salary',
    BONUS: 'Bonus',
    INVESTMENT: 'Investment Income',
    RENTAL: 'Rental Income',
    OTHER: 'Other Income',
  },
  EXPENSES: {
    FOOD: 'Food & Dining',
    GROCERIES: 'Groceries',
    TRANSPORTATION: 'Transportation',
    GAS: 'Gas & Fuel',
    SHOPPING: 'Shopping',
    ENTERTAINMENT: 'Entertainment',
    BILLS: 'Bills & Utilities',
    HEALTHCARE: 'Healthcare',
    EDUCATION: 'Education',
    TRAVEL: 'Travel',
    FEES: 'Fees & Charges',
    OTHER: 'Other Expenses',
  },
  TRANSFERS: {
    INTERNAL: 'Internal Transfer',
    EXTERNAL: 'External Transfer',
    PAYMENT: 'Payment',
    DEPOSIT: 'Deposit',
    WITHDRAWAL: 'Withdrawal',
  },
} 

// Date Formats
export const DATE_FORMATS = {
  ISO: 'YYYY-MM-DD',
  ISO_DATETIME: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  DISPLAY_DATE: 'MMM D, YYYY',
  DISPLAY_DATETIME: 'MMM D, YYYY h:mm A',
  SHORT_DATE: 'MM/DD/YYYY',
  MONTH_YEAR: 'MMMM YYYY',
} 

// Currency Codes
export const CURRENCIES = {
  USD: 'USD',
  EUR: 'EUR',
  GBP: 'GBP',
  CAD: 'CAD',
  AUD: 'AUD',
  JPY: 'JPY',
  CHF: 'CHF',
  CNY: 'CNY',
  INR: 'INR',
} 



// File Upload
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
} as const

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_PREFERENCES: 'userPreferences',
  THEME: 'theme',
  LANGUAGE: 'language',
} as const

// Theme Constants
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} 

// Regular Expressions
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[\d\s\-\(\)]+$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  ACCOUNT_NUMBER: /^[0-9X*]{4,}$/,
} 

// Yodlee Constants
export const YODLEE = {
  BASE_URLS: {
    DEVELOPMENT: 'https://development.api.yodlee.com/ysl',
    SANDBOX: 'https://sandbox.api.yodlee.com/ysl',
    PRODUCTION: 'https://production.api.yodlee.com/ysl',
  },
  FASTLINK_URLS: {
    DEVELOPMENT: 'https://fl4.yodlee.com/authenticate/restserver',
    SANDBOX: 'https://fl3.yodlee.com/authenticate/restserver',
    PRODUCTION: 'https://fl.yodlee.com/authenticate/restserver',
  },
  REFRESH_STATUSES: {
    SUCCESS: 'SUCCESS',
    IN_PROGRESS: 'IN_PROGRESS',
    FAILED: 'FAILED',
    PARTIAL_SUCCESS: 'PARTIAL_SUCCESS',
    LOGIN_IN_PROGRESS: 'LOGIN_IN_PROGRESS',
    SCHEDULED: 'SCHEDULED',
  },
} as const

// Time Constants
export const TIME = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  MONTH: 30 * 24 * 60 * 60 * 1000,
  YEAR: 365 * 24 * 60 * 60 * 1000,
} as const

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const

// Application Environment
export const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
  TEST: 'test',
} as const 

export const  PORTAL = {
  OTP:{
      EXPIRY_TIME: 240, // 4 minutes
  }
} as const

// External Links
/**
 * Terms of Use link
 * @see https://solutions.yodlee.com/fastlink-terms_english
 */
export const TERMS_OF_USE_URL = 'https://solutions.yodlee.com/fastlink-terms_english';

/**
 * Privacy Notice link
 * @see https://www.yodlee.com/legal/privacy-notice
 */
export const PRIVACY_NOTICE_URL = 'https://www.yodlee.com/legal/privacy-notice';

/**
 * External link configuration for consistent behavior
 */
export const EXTERNAL_LINK_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;